import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { ChangeStatus } from '../../../../shared/issuesV2';
import { useQuery } from '../../../contexts/RouterContext';
import { useOnMount } from '../../../hooks';
import useChangeStatusSummary from '../../../hooks/api/issues/useChangeStatusSummary';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { Badge, icons, IconText } from '../../generic';
import Flex from '../../generic/Flex';
import SidebarNavLayout from '../../layouts/SidebarNavLayout';
import IssuesInbox from '../IssuesInbox';
import useFetchIssuesAction from '../IssuesInbox/hooks/useFetchIssuesAction';
import type { IssuesContainerProps } from '../IssuesContainer';

const ComparisonContent: VoidFunctionComponent<IssuesContainerProps> = ({
  params,
  location,
  project,
  revision,
  router,
}) => {
  const dispatch = useDispatch();
  const issueFilterQuery = useSelector(IssuesV2Store.selectors.getIssuesFilterQuery);
  const changeStatus = useSelector(IssuesV2Store.selectors.getChangeStatus) as ChangeStatus;

  const setChangeStatus = useFetchIssuesAction(IssuesV2Store.actions.setComparisonChangeStatus);

  const [urlChangeStatus] = useQuery<{ scope: { compareTo: { changeStatus: ChangeStatus } } }>(
    (query) => ({
      scope: {
        compareTo: {
          changeStatus: query?.scope?.compareTo?.changeStatus,
        },
      },
    }),
  );

  useOnMount(() => {
    if (urlChangeStatus?.scope?.compareTo?.changeStatus) {
      setChangeStatus(urlChangeStatus.scope.compareTo.changeStatus);
    }
  });

  const { data: changeStatusSummary } = useChangeStatusSummary(issueFilterQuery);

  const changeChangeStatus = (newChangeStatus: ChangeStatus) => (e: MouseEvent) => {
    e.preventDefault();
    setChangeStatus(newChangeStatus);
    dispatch(IssuesV2Store.actions.setPage(1));
  };

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav>
        <SidebarNavLayout.NavHeading>Issues</SidebarNavLayout.NavHeading>
        <SidebarNavLayout.Nav.Item
          to="#"
          onClick={changeChangeStatus(ChangeStatus.New)}
          isActive={changeStatus === ChangeStatus.New}
        >
          <Flex justify="space-between" align="center">
            <span>
              <IconText icon={icons.plus} text="New" />
            </span>
            <Badge color="light-gray" shape="circle" size="micro">
              {changeStatusSummary?.issues.new ?? '...'}
            </Badge>
          </Flex>
        </SidebarNavLayout.Nav.Item>
        <SidebarNavLayout.Nav.Item
          to="#"
          onClick={changeChangeStatus(ChangeStatus.Remediated)}
          isActive={changeStatus === ChangeStatus.Remediated}
        >
          <Flex justify="space-between" align="center">
            <span>
              <IconText icon={icons.minus} text="Remediated" />
            </span>
            <Badge color="light-gray" shape="circle" size="micro">
              {changeStatusSummary?.issues.remediated ?? '...'}
            </Badge>
          </Flex>
        </SidebarNavLayout.Nav.Item>
        <SidebarNavLayout.NavItem
          to="#"
          onClick={changeChangeStatus(ChangeStatus.Unchanged)}
          isActive={changeStatus === ChangeStatus.Unchanged}
        >
          <Flex justify="space-between" align="center">
            <span>
              <IconText icon={icons.arrowLeftRight} text="Unchanged" />
            </span>
            <Badge color="light-gray" shape="circle" size="micro">
              {changeStatusSummary?.issues.unchanged ?? '...'}
            </Badge>
          </Flex>
        </SidebarNavLayout.NavItem>
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        <IssuesInbox
          params={params}
          location={location}
          project={project}
          revision={revision}
          router={router}
          withNoPadding
        />
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

export default ComparisonContent;
